import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useMemoizedFn } from 'ahooks';
import {
  Button,
  DialogPlugin,
  Dropdown,
  Form,
  Input,
  MessagePlugin,
  Select,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchTenantOptions } from '@/slices/namespaceSlice';
import useTable from '@/hooks/useTable';
import { clearBackLog, getNamespaceList, unloadNamespace } from '@/services/namespaceService';
import {
  deal70101,
  renderByteRate,
  renderDateTime,
  renderNum,
  renderNumRate,
  toTsOptions,
} from '@/utils/common';
import ColTip from '@/components/Tip';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import PopupLink from '@/components/Cluster/PopupLink';
import { fetchClusterDetailByName } from '@/slices/clusterSlice';
import PermWrapper from '@/components/PermWrapper';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import TopicTip from '@/components/Cluster/TopicTip';
import FormModal from './FormModal';
import Detail from './Detail';
import Dashboard from './Dashboard';

const { FormItem } = Form;

function Body({ fetchTenantOptions, clusterName, tenantOptions, fetchClusterDetailByName, perms }) {
  const [formVisible, setFormVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const navigate = useNavigate();

  const confirmBtn = useDisableButton({
    content: '确定',
  });
  // 查询初始化
  const initialData = useMemo(
    () => ({
      tenant: searchParams.get('tenant'),
      namespace: searchParams.get('namespace'),
    }),
    [searchParams],
  );

  const options = [
    {
      content: '清除积压',
      resource: 'pulsar:namespace:list:clearBacklog',
      value: 1,
    },
    {
      content: '手动卸载',
      resource: 'pulsar:namespace:list:uninstall',
      value: 2,
    },
  ].filter((item) => perms.includes(item.resource));

  const columns = [
    {
      colKey: 'namespace',
      title: '命名空间',
      sorter: true,
      width: 200,
      cell: ({ row }) => (
        <Detail namespace={row.namespace} clusterName={clusterName} onDelete={doRefresh} />
      ),
    },
    {
      colKey: 'topicPartitionCount',
      width: 100,
      sorter: true,
      title: <TopicTip>Topic</TopicTip>,
      cell: ({ row }) => {
        const showLink = row.topicPartitionCount && row.topicCount;
        const content = `${row.topicPartitionCount}/${row.topicCount}`;
        return showLink ? (
          <PopupLink
            tip='跳转Topic'
            onClick={() => {
              navigate(
                `/cluster/topic/list?name=${clusterName}&tenant=${row.tenant}&namespace=${row.namespace}`,
              );
            }}
          >
            {content}
          </PopupLink>
        ) : (
          content
        );
      },
    },
    {
      colKey: 'createByPlatform',
      sorter: true,
      defaultHide: true,
      title: (
        <ColTip
          content={
            <>
              <strong>平台创建</strong>
              <li>是，代表为管控台创建。</li>
              <li>否，代表由系统自动创建，或者通过Pulsar admin命令创建。</li>
            </>
          }
        >
          平台创建
        </ColTip>
      ),
      cell: ({ row }) => (row.createByPlatform ? '是' : '否'),
      width: 130,
    },
    // {
    //   width: 110,
    //   title: (
    //     <ColTip
    //       content={
    //         <>
    //           <strong>Bundles</strong>
    //           <li>Pulsar集群中负载均衡最小粒度。</li>
    //         </>
    //       }
    //     >
    //       Bundles
    //     </ColTip>
    //   ),
    //   colKey: 'bundleCount',
    //   sorter: true,
    //   defaultHide: true,
    // },
    {
      title: '生产吞吐',
      colKey: 'bytesIn',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderByteRate,
    },
    {
      title: '消费吞吐',
      colKey: 'bytesOut',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderByteRate,
    },
    {
      title: '生产速率',
      colKey: 'rateIn',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderNumRate,
    },
    {
      title: '消费速率',
      colKey: 'rateOut',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderNumRate,
    },
    {
      title: (
        <ColTip
          content={
            <>
              <strong>积压条数</strong>
              <li>获取消费最慢的订阅者积压值。</li>
            </>
          }
        >
          积压条数
        </ColTip>
      ),
      colKey: 'backlog',
      sorter: true,
      cell: renderNum,
      width: 130,
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      sorter: true,
      defaultHide: true,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'updateTime',
      sorter: true,
      cell: renderDateTime,
      // sorter: (a, b) => a.updateTime.localeCompare(b.updateTime, 'en'),
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      width: 180,
    },
    {
      title: '归属应用',
      colKey: 'app',
      sorter: true,
      defaultHide: true,
      width: 120,
    },
    {
      title: '备注',
      colKey: 'note',
      ellipsis: true,
      defaultHide: true,
      width: 260,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            size={18}
            onClick={() => {
              setVisible(true);
              setCurrentRow(row);
            }}
          />
          <Dropdown options={options} onClick={({ value }) => clickHandler(value, row)}>
            <span>
              <Icon size={18} type='dots-vertical' />
            </span>
          </Dropdown>
        </Space>
      ),
      width: 80,
    },
  ];

  const onConfirm = async (dialog, row) => {
    try {
      dialog.update({
        confirmBtn: { loading: true, content: '确定' },
      });
      const { meta } = await clearBackLog({ clusterName, namespace: row.namespace });
      // 主集群创建的资源已在灾备集群存在
      if (meta.code === 70101) {
        deal70101(meta.publicMessage, () => onConfirm(dialog, row), {
          url: '/cluster/namespace/list',
          namespace: row.namespace,
        });

        return;
      }
      MessagePlugin.success('操作成功！');
      dialog.hide();
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      dialog.update({
        confirmBtn: { loading: false, content: '确定' },
      });
    }
  };

  const clickHandler = async (value, row) => {
    if (value === 1) {
      const dialog = DialogPlugin.confirm({
        header: '清除积压',
        theme: 'warning',
        body: (
          <Space breakLine size={[6, 6]}>
            清除命名空间
            <Tag>{row.namespace}</Tag>
            下所有消费者积压。
          </Space>
        ),
        confirmBtn,
        onClose: () => dialog.hide(),
        onConfirm: () => onConfirm(dialog, row),
      });
    }

    if (value === 2) {
      const dialog = DialogPlugin.confirm({
        header: '手动卸载',
        theme: 'warning',
        body: '手动出触发命名空间级别unload操作，此操作可以让该命名空间下的bundle重新分配，暂时缓解broker负载不均的问题。适用于某个broker没有任何流量的场景。',
        confirmBtn,
        onClose: () => dialog.hide(),
        onConfirm: async () => {
          try {
            dialog.update({
              confirmBtn: { loading: true, content: '确定' },
            });
            await unloadNamespace({ clusterName, namespace: row.namespace });
            MessagePlugin.success('操作成功！');
            dialog.hide(); // 关闭弹窗
            doRefresh();
          } catch (error) {
            console.trace(error);
            throw error;
          } finally {
            dialog.update({
              confirmBtn: { loading: false, content: '确定' },
            });
          }
        },
      });
    }
  };

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getNamespaceList({ ...params, clusterName });
    setData(data);
  });

  const { setData, header, config, doSearch, doRefresh, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='tenant'>
          <Select options={toTsOptions(tenantOptions)} placeholder='请选择租户' clearable />
        </FormItem>
        <FormItem name='namespace'>
          <Input placeholder='请输入命名空间' clearable />
        </FormItem>
        <FormItem name='app'>
          <Input placeholder='请输入应用名称' clearable />
        </FormItem>
      </>
    ),
    operators: (
      <PermWrapper resource='pulsar:namespace:list:create'>
        <Button
          theme='primary'
          onClick={() => {
            setFormVisible(true);
          }}
        >
          创建命名空间
        </Button>
      </PermWrapper>
    ),
  });

  const doInit = useMemoizedFn(async () => {
    form.reset();
    await fetchTenantOptions({
      clusterName,
      type: 'tenant',
    });
    fetchClusterDetailByName(clusterName);
    // 有初始化条件
    if (searchParams.size > 1) {
      await doSearch(initialData);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [clusterName, doInit]);

  const handleClose = () => {
    setVisible(false);
  };

  const modalClose = (refresh) => {
    setFormVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        handleClose={handleClose}
      />
      <FormModal visible={formVisible} clusterName={clusterName} handleClose={modalClose} />
    </Root>
  );
}

export default connect(
  (state) => ({
    perms: state.auth.perms,
    tenantOptions: state.namespace.tenantOptions,
  }),
  {
    fetchTenantOptions,
    fetchClusterDetailByName,
  },
)(Body);

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
